import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyStatus = 'idle' | 'copied' | 'error';

// Clipboard copy with a transient status for in-place button feedback.
export function useCopyToClipboard() {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const operationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const copy = useCallback(
    (text: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const operation = ++operationRef.current;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          if (mountedRef.current && operation === operationRef.current) setStatus('copied');
        })
        .catch(() => {
          if (mountedRef.current && operation === operationRef.current) setStatus('error');
        })
        .finally(() => {
          if (!mountedRef.current || operation !== operationRef.current) return;
          timerRef.current = setTimeout(() => setStatus('idle'), 2000);
        });
    },
    []
  );

  return { copy, status };
}
